import { Component, Provide, Ref, Vue } from 'vue-property-decorator'
import PdfView from '@/components/pdf-view/pdf-view.vue'
import {
    initialReportsModalForm,
    RangeTypeEnum,
    ReportsModalFormState,
    ReportTypeEnum
} from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/reports-modal.model'
import ReportsModalFormRangeFilterComponent
    from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-range-filter/reports-modal-form-range-filter.vue'
import ReportsModalFormRangeFilter
    from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-range-filter/reports-modal-form-range-filter'
import {
    ReportsModalFormRangeFilterState
} from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-range-filter/reports-modal-form-range-filter.model'
import ReportsModalFormContentFilterComponent
    from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-content-filter/reports-modal-form-content-filter.vue'
import ReportsModalFormContentFilter
    from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-content-filter/reports-modal-form-content-filter'
import {
    FilterByAnaReportEnum,
    ReportModalFormContentFilter,
    ReportsModalFormContentFilterState
} from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-content-filter/reports-modal-form-content-filter.model'
import {
    batchReportServices
} from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/services/batch-report.service'
import { CommonResource } from '@/api/resources/common/shared/models/common.models'
import { BModal } from 'bootstrap-vue'
import { CustomValidationUtils } from '@/shared/util/custom-validation-utils'
import { GridUtils } from '@/shared/util/grid-utils'
import { ISimpleFilterModel } from '@ag-grid-community/core/dist/cjs/filter/provided/simpleFilter'
import { DateUtils } from '@/shared/util/date-utils'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        PdfView,
        ReportsModalFormRangeFilterComponent,
        ReportsModalFormContentFilterComponent
    }
})
export default class ReportsModalForm extends Vue {
    @Ref() readonly reportsModalForm!: BModal
    @Ref() readonly reportsModalFormRangeFilter!: ReportsModalFormRangeFilter
    @Ref() readonly reportsModalFormContentFilter!: ReportsModalFormContentFilter

    @Provide() externalValidator = this.$validator

    state: ReportsModalFormState
    constructor() {
        super()
        this.state = initialReportsModalForm()
    }

    created() {
        this.$validator.extend('case_number', {
            getMessage: field => this.$tc('registry.errors.invalidCaseLabel'),
            validate: value => CustomValidationUtils.checkCaseLabelRegex(value)
        })
    }

    show(): void {
        this.reportsModalForm.show()
    }

    hide(): void {
        this.cleanComponentStates()
        this.$_resetValidator(this.$validator)
        this.reportsModalForm.hide()
    }

    cleanComponentStates(forceResetDates: boolean = false): void {
        this.reportsModalFormRangeFilter.initializeState(forceResetDates)
        this.reportsModalFormContentFilter.initializeState()
        this.state = initialReportsModalForm()
    }

    onReportsModalFormRangeFilterStateChanged(reportsModalFormRangeFilterState: ReportsModalFormRangeFilterState): void {
        this.state.range = reportsModalFormRangeFilterState
    }

    onReportsModalFormContentFilterChanged(reportsModalFormContentFilterState: ReportsModalFormContentFilterState): void {
        this.state.content = reportsModalFormContentFilterState
    }

    async onGenerateButtonClick(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            this.setFilterModel()
            if (this.isApaReports) {
                this.state.pdfB64 = await this.generateReportsFromApaReports(true)
            } else if (this.isPatientCasesReport) {
                const params = {
                    sort: this.state.content.showBy?.id
                }
                this.state.pdfB64 = await this.generateReportsFromPatientsCases(params, true)
            }
        } else {
            this.$_showErrorModal(this.$tc('studyRequest.general.requiredFieldsNotice'))
        }
    }

    async onCleanButtonClick(): Promise<void> {
        this.cleanComponentStates(true)
        await this.$validator.reset()
    }

    private _getFormattedDate(date: StrictNullable<Date>):StrictNullable<string> {
        return DateUtils.formatDate(date, DateUtils.zonedDateTimePattern)
    }

    setFilterModel(): void {
        let filterModel: any = {}
        if (this.state.range.rangeType?.id === RangeTypeEnum.REQUEST_DATE) {
            filterModel.casRequestDate = {
                dateFrom: this._getFormattedDate(this.state.range.startDate),
                dateTo: this._getFormattedDate(this.state.range.endDate),
                type: 'inRange',
                filterType: 'datetime'
            }
        }
        if (this.state.range.rangeType?.id === RangeTypeEnum.REGISTRY_DATE) {
            filterModel.casRegistrationDate = {
                dateFrom: this._getFormattedDate(this.state.range.startDate),
                dateTo: this._getFormattedDate(this.state.range.endDate),
                type: 'inRange',
                filterType: 'datetime'
            }
        }
        if (this.state.range.rangeType?.id === RangeTypeEnum.INFORMED_DATE) {
            filterModel.casInformedDate = {
                dateFrom: this._getFormattedDate(this.state.range.startDate),
                dateTo: this._getFormattedDate(this.state.range.endDate),
                type: 'inRange',
                filterType: 'datetime'
            }
        }
        if (this.state.range.rangeType?.id === RangeTypeEnum.VALIDATED_DATE) {
            filterModel.casValidationDate = {
                dateFrom: this._getFormattedDate(this.state.range.startDate),
                dateTo: this._getFormattedDate(this.state.range.endDate),
                type: 'inRange',
                filterType: 'datetime'
            }
        }
        if (this.state.range.rangeType?.id === RangeTypeEnum.CASE_NUMBER) {
            if (this.state.range.caseType) {
                filterModel = {
                    'caseType.id': GridUtils.getNumberFilterModel('equals', this.state.range.caseType.id),
                    'casCasenumber': {
                        filter: this.state.range.startNumeric,
                        filterTo: this.state.range.endNumeric,
                        filterType: 'text',
                        type: 'inRange'
                    }
                }
            } else {
                filterModel = {
                    'casCasenumber': {
                        filter: this.state.range.startNumeric,
                        filterTo: this.state.range.endNumeric,
                        filterType: 'text',
                        type: 'inRange'
                    }
                }
            }
            if (!filterModel.casCasenumber.filter && !filterModel.casCasenumber.filterTo) {
                delete filterModel.casCasenumber
            }
        }
        if (this.state.reportType.id === ReportTypeEnum.APA_REPORT) {
            this.state.content.filters.forEach((filter: ReportModalFormContentFilter) => {
                let filterToAdd: Record<string, ISimpleFilterModel> = {}
                if (filter.id === FilterByAnaReportEnum.DESTINATION_PROFESSIONAL) {
                    filterToAdd = {
                        [filter.id]: GridUtils.getTextFilterModel('contains', filter.value)
                    }
                } else {
                    if (filter.values) {
                        filterToAdd = {
                            [filter.id]: GridUtils.getSetFilterModel(filter.values.map(value => value.id.toString()))
                        }
                    }
                }
                filterModel = {
                    ...filterModel,
                    ...filterToAdd
                }
            })
        }
        this.state.filterModel = filterModel
    }
    async generateReportsFromApaReports(encoded: boolean): Promise<CommonResource | null> {
        return this.$_toCallUnique(batchReportServices.generateReportsFromApaReports(
            this.state.content.caseStatus?.code!,
            encoded,
            this.state.filterModel,
            this.laboratoryId
        ))
    }

    async generateReportsFromPatientsCases(params: any, encoded: boolean): Promise<CommonResource | null> {
        return this.$_toCallUnique(batchReportServices.generateReportsFromPatientsCases(
            this.state.content.caseStatus?.code!,
            encoded,
            this.state.filterModel,
            this.laboratoryId,
            params
        ))
    }

    get laboratoryId(): number {
        return this.$store.getters.labId
    }

    get isPatientCasesReport(): boolean {
        return this.state.reportType?.id === ReportTypeEnum.PATIENT_CASES
    }

    get isApaReports(): boolean {
        return this.state.reportType?.id === ReportTypeEnum.APA_REPORT
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }
}
